/** The feature-flag waterfall for one block's feature.
 *
 *  Six levels, evaluated broadest → narrowest; at render time the first
 *  *applicable* disabled level decides. This view shows **which rows exist**,
 *  never a verdict — a verdict needs a rendering context, and the endpoint
 *  deliberately has none. Two consequences are drawn explicitly rather than
 *  left to the reader:
 *
 *  - `ota` / `tour` rows depend on that context, so they are counted, not listed.
 *  - The `tag` level's `enabled` is **inverted**: true means "condition
 *    enforced", not "feature on". That trap is called out inline, because
 *    every other level reads the opposite way.
 *
 *  For an actual contextual verdict, ops uses the Feature Flags page's
 *  Validate tool — linked at the foot.
 */
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import RemoveIcon from '@mui/icons-material/Remove';
import { Box, Chip, Link, Stack, Tooltip, Typography } from '@mui/material';

import type { AnatomyGateLevel } from '@/api/notificationAnatomy';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { featureRowActions } from '../notifications/notificationLinks';

/** Levels whose rows can be scoped exactly without a rendering context. */
const LISTED = new Set(['system', 'tenant', 'notification', 'tag']);

function LevelRow({
  level,
  last,
  feature,
}: {
  level: AnatomyGateLevel;
  last: boolean;
  feature: string;
}) {
  const configured = level.row_count > 0;
  const listed = LISTED.has(level.level);
  const offRows = level.rows.filter((r) => !r.enabled);

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
      {/* The spine: a dot per level, joined by a hairline. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          alignSelf: 'stretch',
          pt: 0.5,
        }}
      >
        {!configured ? (
          <Tooltip title="No flag row at this level — a vacuous pass">
            <RemoveIcon fontSize="small" color="disabled" />
          </Tooltip>
        ) : offRows.length ? (
          <HighlightOffIcon fontSize="small" color="error" />
        ) : (
          <CheckCircleOutlineIcon fontSize="small" color="success" />
        )}
        {!last && <Box sx={{ flexGrow: 1, width: '1px', bgcolor: 'divider', my: 0.25 }} />}
      </Box>

      <Box sx={{ minWidth: 0, pb: last ? 0 : 1 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {level.label}
          </Typography>
          {level.inverted && (
            <Tooltip
              describeChild
              title="At the tag level, enabled = the tag condition is ENFORCED (not 'feature on'). Disabled suspends the condition — a vacuous pass."
            >
              <Chip
                label="inverted"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ height: 18, fontSize: '0.6875rem' }}
              />
            </Tooltip>
          )}
          {!listed && configured && (
            <Chip
              label={`${level.row_count} row${level.row_count === 1 ? '' : 's'} — context-dependent`}
              size="small"
              variant="outlined"
              sx={{ height: 18, fontSize: '0.6875rem' }}
            />
          )}
        </Stack>

        {!configured && (
          <Typography variant="caption" color="text.secondary">
            no row configured
          </Typography>
        )}

        {listed &&
          level.rows.map((row) => (
            <Typography
              key={row.id}
              variant="caption"
              component="div"
              color={row.enabled ? 'text.secondary' : 'error.main'}
            >
              {/* `id=` deep-links this exact flag row on the grid that owns it. */}
              <ValueActions actions={featureRowActions(row, feature, level.level)}>
                #{row.id}
              </ValueActions>{' '}
              {row.enabled ? 'enabled' : 'disabled'}
              {row.tag_match_mode ? ` · match ${row.tag_match_mode}` : ''}
              {row.comment ? ` — ${row.comment}` : ''}
            </Typography>
          ))}
        {level.rows_truncated && (
          <Typography variant="caption" color="text.secondary" component="div">
            … more rows not shown
          </Typography>
        )}
      </Box>
    </Box>
  );
}

interface Props {
  feature: string;
  /** `[]` = no gate. `undefined` = not looked up. */
  levels: AnatomyGateLevel[] | undefined;
}

export function GateWaterfall({ feature, levels }: Props) {
  if (levels === undefined) {
    return (
      <Typography variant="caption" color="text.secondary">
        Feature gates not loaded.
      </Typography>
    );
  }
  if (!levels.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        This block is not gated behind a feature.
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Configured gates for <strong>{feature}</strong>, broadest first. This is what exists —
        not a verdict: the deciding level depends on the rendering context.
      </Typography>
      {levels.map((level, i) => (
        <LevelRow
          key={level.level}
          level={level}
          last={i === levels.length - 1}
          feature={feature}
        />
      ))}
      <Link
        // The feature-flags grid is a QuerySearch page: it reads `?q=<DSL>`
        // only. A bare `?feature=` param is ignored and lands on the
        // unfiltered grid.
        href={`/notifications/features?q=${encodeURIComponent(`feature=${feature}`)}`}
        target="_blank"
        rel="noopener"
        variant="caption"
        sx={{ display: 'inline-block', mt: 1 }}
      >
        Check this feature against a real context →
      </Link>
    </Box>
  );
}
